import { getUserPermissions } from "../Api/RbacApi";
import CODE from "../Static/Constants/StatusCodes";
import AppCache from "../Helper/Common/AppCache";

/**
 * Permission Service
 * Handles all RBAC permission logic with v1/v2 version awareness
 */
class PermissionService {
  static permissionsMap = {};
  static rbacVersion = 'v1'; // Default to old system

  /**
   * Fetch user permissions from backend and store in memory
   * @returns {Promise<Object>} Permissions map
   */
  static async fetchAndStorePermissions() {
    try {
      const response = await getUserPermissions();

      if (response?.responseStatus?.code === CODE.SUCCESS) {
        // Note: GetData wraps the backend response, so actual data is in response.response.data
        this.permissionsMap = response.response?.data?.permissionsMap || {};
        console.log('[PermissionService] Permissions loaded:', Object.keys(this.permissionsMap).length, 'features');
        return this.permissionsMap;
      }

      console.warn('[PermissionService] Failed to fetch permissions, using empty map');
      return {};
    } catch (error) {
      console.error('[PermissionService] Error fetching permissions:', error);
      return {};
    }
  }

  /**
   * Check if user has a specific permission
   * @param {string} featureCode - Feature code (e.g., 'DIGITAL_TWIN')
   * @param {string} operation - Operation type ('view', 'create', 'update', 'delete')
   * @returns {boolean} True if user has permission
   */
  static hasPermission(featureCode, operation = 'view') {
    // Check RBAC version (default to v1 if not set)
    const rbacVersion = AppCache.getItem('rbacVersion') || 'v1';
    const rbacEnabled = AppCache.getItem('rbacEnabled') ?? false;

    // V2: Use new RBAC system
    if (rbacVersion === 'v2' && rbacEnabled) {
      const permissions = this.permissionsMap[featureCode] || [];
      const hasPermission = permissions.includes(operation);

      if (!hasPermission) {
        console.warn(`[PermissionService] Permission denied: ${featureCode}.${operation}`);
      }

      return hasPermission;
    }

    // V1: Return true (old system handles permission checks via props)
    return true;
  }

  /**
   * Check if user has any of the specified permissions
   * @param {string} featureCode - Feature code
   * @param {Array<string>} operations - Array of operations to check
   * @returns {boolean} True if user has any of the permissions
   */
  static hasAnyPermission(featureCode, operations = ['view']) {
    const rbacVersion = AppCache.getItem('rbacVersion') || 'v1';
    const rbacEnabled = AppCache.getItem('rbacEnabled') ?? false;

    // V2: Check if user has any of the operations
    if (rbacVersion === 'v2' && rbacEnabled) {
      return operations.some(op => this.hasPermission(featureCode, op));
    }

    // V1: Return true
    return true;
  }

  /**
   * Get all permissions for a feature
   * @param {string} featureCode - Feature code
   * @returns {Array<string>} Array of operations user can perform
   */
  static getFeaturePermissions(featureCode) {
    const rbacVersion = AppCache.getItem('rbacVersion') || 'v1';
    const rbacEnabled = AppCache.getItem('rbacEnabled') ?? false;

    // V2: Return feature permissions
    if (rbacVersion === 'v2' && rbacEnabled) {
      return this.permissionsMap[featureCode] || [];
    }

    // V1: Return empty array
    return [];
  }

  /**
   * Check if RBAC v2 is enabled
   * @returns {boolean} True if v2 is enabled
   */
  static isRbacV2Enabled() {
    const rbacVersion = AppCache.getItem('rbacVersion') || 'v1';
    const rbacEnabled = AppCache.getItem('rbacEnabled') ?? false;
    return rbacVersion === 'v2' && rbacEnabled;
  }

  /**
   * Clear all stored permissions
   */
  static clearPermissions() {
    this.permissionsMap = {};
    this.rbacVersion = 'v1';
  }

  /**
   * Get all loaded permissions (for debugging)
   * @returns {Object} Permissions map
   */
  static getAllPermissions() {
    return { ...this.permissionsMap };
  }
}

export default PermissionService;
